import React from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Session, SessionStatus, StudyType } from '@app/api';
import { Theme } from '@app/theme';
import {
  FormatIcon,
  GenericButton,
  GenericButtonProps,
  ProgressLine,
  RoundBgIcon,
  TextLoadingPlaceholder,
  useSesionMinAndMaxDatesString,
  getAllowedFormats,
} from '@app/core';

import { StatusLabel } from 'components/Session/StatusLabel';

const createSmallStyles = (theme: Theme) => ({
  container: {
    height: 'auto',
    padding: [[0, 0, theme.marginBase * 3, theme.marginBase * 3]],
    flexWrap: 'wrap',
  },
  block: {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginRight: theme.marginBase * 3,
    marginTop: theme.marginBase * 3,
  },
  statusBlock: {
    marginLeft: 0,
  },
  textBlock: {
    flex: 'none',
    width: '100%',
  },
  datesBlock: {
    flex: 1,
    alignSelf: 'center',
  },
  formatBlock: {
    justifyContent: 'flex-end',
    minWidth: 0,
  },
});

const useStyles = createUseStyles((theme: Theme) => {
  const smallStyles = createSmallStyles(theme);
  return {
    container: {
      display: 'flex',
      width: '100%',
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.std,
      transition: [['box-shadow', '.1s']],
      textAlign: 'left',

      height: theme.marginBase * 11,
      padding: 0,
      flexWrap: 'no-wrap',

      [theme.media.tablet]: smallStyles.container,
      '&:hover': {
        boxShadow: theme.boxShadow.base,
      },
    },
    containerSmall: smallStyles.container,
    containerOverWhite: {
      border: {
        width: 1,
        style: 'solid',
        color: theme.colors.grayBorder,
      },
    },
    containerSelected: {
      backgroundColor: theme.colors.grayBackground,
    },

    block: {
      display: 'flex',

      alignItems: 'center',
      justifyContent: 'center',
      marginRight: theme.marginBase * 5,

      [theme.media.tablet]: smallStyles.block,
    },
    blockSmall: smallStyles.block,

    statusBlock: {
      minWidth: theme.marginBase * 13,
      marginLeft: theme.marginBase * 3,
      [theme.media.tablet]: smallStyles.statusBlock,
    },
    statusBlockSmall: smallStyles.statusBlock,

    textBlock: {
      justifyContent: 'flex-start',
      flex: 1,

      [theme.media.tablet]: smallStyles.textBlock,
    },
    textBlockSmall: smallStyles.textBlock,

    title: {
      ...theme.fonts.label,
      color: theme.colors.inkSecondary,
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      display: '-webkit-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxHeight: theme.marginBase * 7,
    },
    participantsBlock: {
      minWidth: theme.marginBase * 9,
    },
    datesBlock: {
      ...theme.fonts.caption,
      color: theme.colors.inkSecondary,
      minWidth: theme.marginBase * 17,

      [theme.media.tablet]: smallStyles.datesBlock,
    },
    datesBlockSmall: smallStyles.datesBlock,

    formatBlock: {
      marginRight: theme.marginBase * 2,

      minWidth: theme.marginBase * 15,
      justifyContent: 'center',

      [theme.media.tablet]: smallStyles.formatBlock,

      '& > *': {
        marginRight: theme.marginBase,
      },
    },
    formatBlockSmall: smallStyles.formatBlock,
  };
});

interface Props {
  session?: Session;
  className?: string;
  small?: boolean;
  overWhite?: boolean;
  noStatus?: boolean;
  selected?: boolean;
}

export const SessionItem = ({
  className,
  session,
  small,
  noStatus,
  overWhite,
  selected,
  ...buttonProps
}: Props & GenericButtonProps) => {
  const classes = useStyles();
  const minMaxDates = useSesionMinAndMaxDatesString(session);

  const blockClass = classnames(classes.block, {
    [classes.blockSmall]: small,
  });

  return (
    <GenericButton
      className={classnames(
        classes.container,
        {
          [classes.containerSmall]: !!small,
          [classes.containerOverWhite]: !!overWhite,
          [classes.containerSelected]: !!selected,
        },
        className,
      )}
      {...buttonProps}
    >
      {!noStatus && (
        <div
          className={classnames(blockClass, classes.statusBlock, {
            [classes.statusBlockSmall]: small,
          })}
        >
          {session ? (
            <StatusLabel status={session.status} />
          ) : (
            <TextLoadingPlaceholder sample="EN PAUSE " />
          )}
        </div>
      )}
      <div
        className={classnames(blockClass, classes.textBlock, {
          [classes.textBlockSmall]: small,
        })}
      >
        <div className={classes.title}>
          {session ? (
            session.internalName || session.title
          ) : (
            <TextLoadingPlaceholder sample="Envie d'en savoir un peu plus sur Tandemz lors d'un entretien?" />
          )}
        </div>
      </div>
      <div className={classnames(blockClass, classes.participantsBlock)}>
        {session && !!session.numberOfParticipantRequired && (
          <ProgressLine
            color={
              session.status === SessionStatus.DRAFT
                ? 'inkSecondary'
                : undefined
            }
            current={session.numberOfBooked}
            max={session.numberOfParticipantRequired}
          />
        )}
      </div>
      <div
        className={classnames(blockClass, classes.datesBlock, {
          [classes.datesBlockSmall]: small,
        })}
      >
        {session ? (
          minMaxDates
        ) : (
          <TextLoadingPlaceholder sample="25/01 - 18/05" />
        )}
      </div>
      <div
        className={classnames(blockClass, classes.formatBlock, {
          [classes.formatBlockSmall]: small,
        })}
      >
        {session &&
          (session?.studyType === StudyType.UNMODERATED ? (
            <RoundBgIcon icon="ri-list-check-2" key="question" />
          ) : (
            getAllowedFormats(session).map((format) => (
              <FormatIcon key={format} format={format} />
            ))
          ))}
      </div>
    </GenericButton>
  );
};
